import { convertFileSrc } from "@tauri-apps/api/core";
import { open } from "@tauri-apps/plugin-dialog";
import { invoke, tauriOnlyInvoke } from "../invoke";
import { openDialogDefaults } from "../dialogDefaults";
import { resolveHomeDir } from "./dialogHome";
import { isTauri, joinProjectPath } from "./core";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "avif"];

function imageExtension(sourcePath: string, fallback: string): string {
  const base = sourcePath.split(/[/\\]/).pop() ?? fallback;
  const dot = base.lastIndexOf(".");
  return dot > 0
    ? base
        .slice(dot + 1)
        .toLowerCase()
        .replace("jpeg", "jpg")
    : "jpg";
}

export function coverAssetName(sourcePath: string): string {
  return `cover.${imageExtension(sourcePath, "cover.jpg")}`;
}

/** Stable Assets/ name for the project-wide chapter title image. */
export function chapterHeadingAssetName(sourcePath: string): string {
  return `chapter-heading.${imageExtension(sourcePath, "chapter-heading.jpg")}`;
}

export async function pickImageFile(): Promise<string | null> {
  if (isTauri() === false) return null;
  const home = await resolveHomeDir();
  const path = await open({
    ...openDialogDefaults(home),
    title: "Choose image",
    multiple: false,
    filters: [{ name: "Images", extensions: IMAGE_EXTENSIONS }],
  });
  if (path === null || path === "" || Array.isArray(path)) return null;
  return path;
}

/** Copies from an absolute local path chosen via a native dialog (see
 * `pickImageFile`) — no `zw-online` equivalent, `sourcePath` only means
 * anything on the machine running the desktop app. */
export async function importProjectAsset(
  rootPath: string,
  sourcePath: string,
  assetName: string,
): Promise<string> {
  return tauriOnlyInvoke<string>("import_project_asset", {
    rootPath,
    sourcePath,
    assetName,
  });
}

/** Project-relative image paths under Assets/ (sorted). */
export async function listProjectAssets(rootPath: string): Promise<string[]> {
  return invoke<string[]>("list_project_assets", { rootPath });
}

export async function removeProjectAsset(
  rootPath: string,
  relativePath: string,
): Promise<void> {
  await invoke("remove_project_asset", { rootPath, relativePath });
}

export async function readProjectAssetDataUrl(
  rootPath: string,
  relativePath: string,
): Promise<string> {
  return invoke<string>("read_project_asset_data_url", {
    rootPath,
    relativePath,
  });
}

export async function writeProjectAssetBytes(
  rootPath: string,
  relativePath: string,
  dataBase64: string,
): Promise<void> {
  await invoke("write_project_asset_bytes", {
    rootPath,
    relativePath,
    dataBase64,
  });
}

/** Webview URL for a file inside an open project.
 * The asset protocol grants only that project's folder, and only while a
 * window has it open. Dot entries such as `.git` are not served.
 * Map rasters, cover previews, and export embeds load through IPC data URLs
 * (`readProjectAssetDataUrl`) instead of this helper. */
export function projectAssetSrc(
  rootPath: string,
  relativePath: string,
): string {
  if (relativePath === "") return "";
  const absolute = joinProjectPath(rootPath, relativePath);
  return isTauri() ? convertFileSrc(absolute) : absolute;
}

/** Base URL for resolving relative image paths in live preview.
 * Same grant as `projectAssetSrc`: the open project root, with dot entries
 * left unreadable. */
export function livePreviewImageBasePath(
  storageRoot: string | null,
): string | null {
  if (storageRoot === null || storageRoot === "") return null;
  const normalizedRoot = storageRoot.replace(/[/\\]+$/, "");
  if (isTauri() === false) {
    const normalized = normalizedRoot.replace(/\\/g, "/");
    return normalized.endsWith("/") ? normalized : `${normalized}/`;
  }
  return convertFileSrc(normalizedRoot);
}
